/**
 * Where a video's faces are at any moment, and which of them are hidden — to the number, per
 * `/specs/features/faces.spec.md`. The web app reads face maps through this; the Android app
 * finds them, carries a port of this reading in `video/FaceMap.kt`, and both answer
 * `corpus/corpus.json`. How a hidden face is drawn is `shaders/faces.frag`, one file both apps
 * load, so a face looks the same hidden whichever app hid it.
 *
 * A face map is found once, on the phone, and only ever read afterwards: everything that took
 * judgement — which detections were one face, which faces were one person, who the lifter is —
 * has already been decided by the time a map is written down, and what is left is arithmetic.
 */

/**
 * Where a shared copy's faces start, before anybody presses one: every face but the lifter's,
 * every face, or none. The account's setting says which; the export dialog starts from it.
 */
export type FacesDefault = 'others' | 'all' | 'none'

export const FACES_DEFAULTS: readonly FacesDefault[] = ['others', 'all', 'none']

/**
 * One sighting of a face: when, in milliseconds from the head of the video, and where — the
 * centre and the size of the face, each a fraction of the picture as it plays, the right way up,
 * measured from its top left corner.
 */
export type FaceKey = readonly [tMs: number, cx: number, cy: number, w: number, h: number]

/**
 * One person the video shows. A span is a stretch of sightings the person was followed through
 * without a break; between two spans the person was out of sight.
 */
export interface FacePerson {
  readonly id: string
  /** Whether this is who the phone took for the lifter. */
  readonly suggested: boolean
  readonly spans: readonly (readonly FaceKey[])[]
}

/** Every face a video shows, found once off the video whose size it names. */
export interface FaceMap {
  readonly version: 1
  /** How many bytes the video it was found off weighed: a map describes that video and no other. */
  readonly videoBytes: number
  readonly people: readonly FacePerson[]
}

/** A face to hide, as an ellipse: its centre and its two radii, in the map's fractions. */
export interface FaceShape {
  readonly personId: string
  readonly cx: number
  readonly cy: number
  readonly rx: number
  readonly ry: number
}

/**
 * How long a span's first sighting is held before it and its last after it. A face is seen a
 * moment after it arrives and lost a moment before it goes, and those moments are exactly when a
 * face would flash by unhidden.
 */
export const HOLD_MS = 300

/**
 * How far the ellipse reaches past the face, as a share of the face's width and height. A face
 * as found runs brow to chin; hair, ears and a face caught turning are all past it.
 */
export const WIDTH_REACH = 0.8
export const HEIGHT_REACH = 0.9

/** The most faces one picture hides. Past it, the largest are the ones hidden. */
export const MOST_SHAPES = 16

function isFraction(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

function readKey(value: unknown): FaceKey | null {
  if (!Array.isArray(value) || value.length !== 5) {
    return null
  }
  const [tMs, cx, cy, w, h] = value as unknown[]
  if (
    !isFraction(tMs) ||
    !isFraction(cx) ||
    !isFraction(cy) ||
    !isFraction(w) ||
    !isFraction(h) ||
    w <= 0 ||
    h <= 0
  ) {
    return null
  }
  return [tMs, cx, cy, w, h]
}

/**
 * A face map read off whatever JSON came back, or null where it is not one. A map that cannot be
 * read is no map, which a video hiding faces treats as faces not yet found — never as no faces.
 */
export function readFaceMap(value: unknown): FaceMap | null {
  if (typeof value !== 'object' || value === null) {
    return null
  }
  const held = value as Record<string, unknown>
  if (held.version !== 1 || !isFraction(held.videoBytes) || !Array.isArray(held.people)) {
    return null
  }
  const people: FacePerson[] = []
  for (const each of held.people as unknown[]) {
    if (typeof each !== 'object' || each === null) {
      return null
    }
    const person = each as Record<string, unknown>
    if (typeof person.id !== 'string' || !Array.isArray(person.spans)) {
      return null
    }
    const spans: FaceKey[][] = []
    for (const span of person.spans as unknown[]) {
      if (!Array.isArray(span) || span.length === 0) {
        return null
      }
      const keys: FaceKey[] = []
      for (const key of span as unknown[]) {
        const read = readKey(key)
        if (!read) {
          return null
        }
        keys.push(read)
      }
      keys.sort((a, b) => a[0] - b[0])
      spans.push(keys)
    }
    people.push({ id: person.id, suggested: person.suggested === true, spans })
  }
  return { version: 1, videoBytes: held.videoBytes, people }
}

/** Whether [map] was found off a video of [videoBytes] bytes — the only video it describes. */
export function describesVideo(map: FaceMap, videoBytes: number | null | undefined): boolean {
  return videoBytes !== null && videoBytes !== undefined && map.videoBytes === videoBytes
}

/** Where one span puts its face at [tMs], or null where the span says nothing then. */
function keyAt(span: readonly FaceKey[], tMs: number): FaceKey | null {
  const first = span[0]
  const last = span[span.length - 1]
  if (!first || !last || tMs < first[0] - HOLD_MS || tMs > last[0] + HOLD_MS) {
    return null
  }
  if (tMs <= first[0]) {
    return first
  }
  if (tMs >= last[0]) {
    return last
  }
  for (let index = 1; index < span.length; index++) {
    const after = span[index]
    const before = span[index - 1]
    if (!after || !before || tMs > after[0]) {
      continue
    }
    const gap = after[0] - before[0]
    const share = gap > 0 ? (tMs - before[0]) / gap : 0
    return [
      tMs,
      before[1] + (after[1] - before[1]) * share,
      before[2] + (after[2] - before[2]) * share,
      before[3] + (after[3] - before[3]) * share,
      before[4] + (after[4] - before[4]) * share,
    ]
  }
  return last
}

/**
 * Every face the video shows at [tMs], whoever it belongs to, in the order the map names its
 * people. A person seen in two spans at once — which only an overlap at their ends can do — is
 * placed by the earlier span.
 */
export function faceShapesAt(map: FaceMap, tMs: number): FaceShape[] {
  const shapes: FaceShape[] = []
  for (const person of map.people) {
    for (const span of person.spans) {
      const key = keyAt(span, tMs)
      if (key) {
        shapes.push({
          personId: person.id,
          cx: key[1],
          cy: key[2],
          rx: key[3] * WIDTH_REACH,
          ry: key[4] * HEIGHT_REACH,
        })
        break
      }
    }
  }
  return shapes
}

/**
 * Whether the phone could not tell who the lifter is: people were seen, and none of them stood
 * clearly ahead. Hiding every face but the lifter's cannot be done without asking, then.
 */
export function lifterUnsure(map: FaceMap): boolean {
  return map.people.length > 0 && !map.people.some((person) => person.suggested)
}

/**
 * The people [start] hides in [map]: every one but the lifter, every one, or none. Where the
 * lifter is unsure, every one is hidden — nobody is shown who was not chosen.
 */
export function hiddenAtStart(map: FaceMap, start: FacesDefault): Set<string> {
  if (start === 'none') {
    return new Set()
  }
  return new Set(
    map.people.filter((person) => start === 'all' || !person.suggested).map((person) => person.id),
  )
}

/**
 * The faces of [hidden] at [tMs], the largest first, and never more than [MOST_SHAPES] of them.
 * Size is the ellipse's area in the map's fractions.
 */
export function hiddenShapesAt(
  map: FaceMap,
  hidden: ReadonlySet<string>,
  tMs: number,
): FaceShape[] {
  return faceShapesAt(map, tMs)
    .filter((shape) => hidden.has(shape.personId))
    .map((shape, order) => ({ shape, order }))
    .sort((a, b) => b.shape.rx * b.shape.ry - a.shape.rx * a.shape.ry || a.order - b.order)
    .slice(0, MOST_SHAPES)
    .map(({ shape }) => shape)
}

/**
 * The people a picker offers, most likely the lifter first: the suggested ones, then the rest by
 * how long they were seen. Each comes with the sighting that best shows them — their largest.
 */
export function peopleToOffer(map: FaceMap): { person: FacePerson; best: FaceKey }[] {
  const seenFor = (person: FacePerson) =>
    person.spans.reduce((sum, span) => {
      const first = span[0]
      const last = span[span.length - 1]
      return first && last ? sum + (last[0] - first[0]) + 1 : sum
    }, 0)
  return map.people
    .map((person, order) => {
      let best: FaceKey | null = null
      for (const span of person.spans) {
        for (const key of span) {
          if (!best || key[3] * key[4] > best[3] * best[4]) {
            best = key
          }
        }
      }
      return { person, best, order, seen: seenFor(person) }
    })
    .filter((each): each is typeof each & { best: FaceKey } => each.best !== null)
    .sort(
      (a, b) =>
        Number(b.person.suggested) - Number(a.person.suggested) ||
        b.seen - a.seen ||
        a.order - b.order,
    )
    .map(({ person, best }) => ({ person, best }))
}
